/**
 * Fair-price view model and helpers (Milestone 9).
 * Single source for badge + detail + filter label mapping.
 */
import { fairValueBadge } from "../components/midpoint/mobility/vehicleMarketFormat";
import {
  FAIR_PRICE_CLASS,
  FAIR_PRICE_CONFIDENCE_ES,
  FAIR_PRICE_DETAIL_TITLES_ES,
  FAIR_PRICE_EXPLANATIONS_ES,
  FAIR_PRICE_LABELS,
  FAIR_PRICE_LABELS_ES,
  FAIR_PRICE_SHORT_LABELS_ES,
} from "../constants/automapFairPriceConstants";

const MOBILITY_LABEL_MAP = {
  "Great Deal": FAIR_PRICE_LABELS.below_market,
  "Great price": FAIR_PRICE_LABELS.below_market,
  "Good Deal": FAIR_PRICE_LABELS.fair_price,
  "Fair Price": FAIR_PRICE_LABELS.fair_price,
  "Fair price": FAIR_PRICE_LABELS.fair_price,
  "High Price": FAIR_PRICE_LABELS.high_price,
  Overpriced: FAIR_PRICE_LABELS.high_price,
};

const SHOW_BADGE_LABELS = new Set([
  FAIR_PRICE_LABELS.below_market,
  FAIR_PRICE_LABELS.fair_price,
  FAIR_PRICE_LABELS.slightly_high,
  FAIR_PRICE_LABELS.high_price,
]);

function normalizeLabelKey(label) {
  if (!label) return FAIR_PRICE_LABELS.unknown;
  const key = String(label).toLowerCase();
  if (Object.values(FAIR_PRICE_LABELS).includes(key)) return key;
  return FAIR_PRICE_LABELS.unknown;
}

export function resolveFairPriceLabel(listing) {
  if (listing?.fairPriceLabel) return normalizeLabelKey(listing.fairPriceLabel);
  const fair = fairValueBadge(listing || {});
  if (fair?.label && MOBILITY_LABEL_MAP[fair.label]) {
    return MOBILITY_LABEL_MAP[fair.label];
  }
  if (listing?.fairValueLabel && MOBILITY_LABEL_MAP[listing.fairValueLabel]) {
    return MOBILITY_LABEL_MAP[listing.fairValueLabel];
  }
  return FAIR_PRICE_LABELS.not_enough_data;
}

function resolveCatalogLabel(t, key, fallback) {
  if (!t) return fallback;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

export function formatFairPriceLabel(label, { short = false, t = null } = {}) {
  const key = normalizeLabelKey(label);
  if (t) {
    const catalogKey = short
      ? `automotive.badges.fairPriceShort.${key}`
      : `automotive.badges.fairPrice.${key}`;
    return resolveCatalogLabel(t, catalogKey, FAIR_PRICE_LABELS_ES[key] || FAIR_PRICE_LABELS_ES.unknown);
  }
  const map = short ? FAIR_PRICE_SHORT_LABELS_ES : FAIR_PRICE_LABELS_ES;
  return map[key] || FAIR_PRICE_LABELS_ES.unknown;
}

export function getFairPriceBadgeTone(label) {
  const key = normalizeLabelKey(label);
  return FAIR_PRICE_CLASS[key] || FAIR_PRICE_CLASS.unknown;
}

export function getComparableCount(listing) {
  return (
    listing?.fairPriceSampleSize ??
    listing?.fairPriceComparableCount ??
    listing?.comparableCount ??
    0
  );
}

export function getFairPriceConfidence(listing) {
  return listing?.fairPriceConfidence ?? listing?.confidence ?? null;
}

export function hasFairPriceComparableData(listing) {
  const label = resolveFairPriceLabel(listing);
  if (label === FAIR_PRICE_LABELS.not_enough_data || label === FAIR_PRICE_LABELS.unknown) {
    return false;
  }
  return getComparableCount(listing) > 0 || SHOW_BADGE_LABELS.has(label);
}

export function shouldShowFairPriceBadge(listing, { allowNoData = false } = {}) {
  const label = resolveFairPriceLabel(listing);
  if (SHOW_BADGE_LABELS.has(label)) return true;
  if (allowNoData && label === FAIR_PRICE_LABELS.not_enough_data) return true;
  return false;
}

function formatCopAmount(value, locale = "es-CO") {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `$${Number(value).toLocaleString(locale, { maximumFractionDigits: 0 })}`;
}

function formatCompactCopRange(min, max, locale = "es-CO") {
  const fmt = (v) => {
    const n = Number(v);
    if (n >= 1_000_000) {
      return `$${(n / 1_000_000).toLocaleString(locale, { maximumFractionDigits: 0 })}M`;
    }
    return formatCopAmount(n, locale);
  };
  if (min != null && max != null) return `${fmt(min)}–${fmt(max)}`;
  return null;
}

/**
 * @param {number|null|undefined} deltaCop
 */
export function formatFairPriceDelta(deltaCop, { t = null, locale = "es-CO" } = {}) {
  if (deltaCop == null || Number.isNaN(Number(deltaCop))) return null;
  const abs = Math.abs(Number(deltaCop));
  const formatted = formatCopAmount(abs, locale);
  if (!formatted) return null;
  if (t) {
    if (deltaCop < 0) {
      return t("automotive.detail.fairPrice.deltaBelow", { amount: formatted });
    }
    if (deltaCop > 0) {
      return t("automotive.detail.fairPrice.deltaAbove", { amount: formatted });
    }
    return null;
  }
  if (deltaCop < 0) {
    return `Aprox. ${formatted} por debajo de referencias similares`;
  }
  if (deltaCop > 0) {
    return `Aprox. ${formatted} por encima de referencias similares`;
  }
  return null;
}

/** @deprecated prefer formatFairPriceDelta(deltaCop) */
export function formatFairPriceDeltaFromListing(listing, options = {}) {
  if (listing?.fairPriceDeltaCop != null) {
    return formatFairPriceDelta(listing.fairPriceDeltaCop, options);
  }
  const pct = listing?.fairPriceDeltaPercent ?? listing?.priceDeltaPercent;
  if (pct == null) return null;
  const abs = Math.abs(pct);
  if (options.t) {
    const direction =
      pct < 0
        ? options.t("automotive.detail.fairPrice.deltaPercentBelow")
        : options.t("automotive.detail.fairPrice.deltaPercentAbove");
    return options.t("automotive.detail.fairPrice.deltaPercent", { percent: abs, direction });
  }
  const direction = pct < 0 ? "por debajo" : "por encima";
  return `Parece estar ${abs}% ${direction} del precio medio observado.`;
}

export function formatComparablePriceLabel(listing, { t = null, locale = "es-CO" } = {}) {
  if (!listing) return null;
  const min =
    listing.comparableMinPriceCop ??
    listing.fairPriceComparableMinCop ??
    listing.comparableMinCop;
  const max =
    listing.comparableMaxPriceCop ??
    listing.fairPriceComparableMaxCop ??
    listing.comparableMaxCop;
  const range = formatCompactCopRange(min, max, locale);
  if (range) {
    return t
      ? t("automotive.detail.fairPrice.comparableRange", { range })
      : `Referencia estimada: ${range}`;
  }

  const median =
    listing.comparableMedianPriceCop ??
    listing.estimatedMarketPriceCop ??
    listing.marketPriceCop ??
    listing.estimatedFairPriceCop;
  if (median != null) {
    const n = Number(median);
    const formatted =
      n >= 1_000_000
        ? `$${(n / 1_000_000).toLocaleString(locale, { maximumFractionDigits: 0 })}M`
        : formatCopAmount(n, locale);
    return t
      ? t("automotive.detail.fairPrice.comparableMedian", { price: formatted })
      : `Precio medio comparable: ${formatted}`;
  }
  return null;
}

export function formatConfidenceLabel(listing, { t = null } = {}) {
  const confidence = getFairPriceConfidence(listing);
  if (!confidence || confidence === "unavailable") return null;
  const key = String(confidence).toLowerCase();
  if (t) {
    const level = resolveCatalogLabel(
      t,
      `automotive.detail.fairPrice.confidence.${key}`,
      FAIR_PRICE_CONFIDENCE_ES[key]
    );
    return level ? t("automotive.detail.fairPrice.confidenceLabel", { level }) : null;
  }
  const es = FAIR_PRICE_CONFIDENCE_ES[key];
  return es ? `Confianza: ${es}` : null;
}

export function getFairPriceBuyerDescription(label, listing, { t = null } = {}) {
  const key = normalizeLabelKey(label || resolveFairPriceLabel(listing));
  if (t) {
    return resolveCatalogLabel(
      t,
      `automotive.detail.fairPrice.explanations.${key}`,
      FAIR_PRICE_EXPLANATIONS_ES[key] || FAIR_PRICE_EXPLANATIONS_ES.unknown
    );
  }
  return FAIR_PRICE_EXPLANATIONS_ES[key] || FAIR_PRICE_EXPLANATIONS_ES.unknown;
}

/** @deprecated use getFairPriceBuyerDescription */
export function getFairPriceExplanation(listing) {
  return getFairPriceBuyerDescription(null, listing);
}

export function getFairPriceLabelText(listing, options) {
  return formatFairPriceLabel(resolveFairPriceLabel(listing), options);
}

/**
 * @param {object|null|undefined} listing
 * @returns {import('./fairPriceViewModelTypes').FairPriceViewModel}
 */
export function getFairPriceViewModel(listing, { t = null, locale = "es-CO" } = {}) {
  const label = resolveFairPriceLabel(listing);
  const sampleSize = getComparableCount(listing);
  const hasEnoughData = hasFairPriceComparableData(listing);
  const formatterOptions = { t, locale };
  const deltaLabel =
    formatFairPriceDelta(listing?.fairPriceDeltaCop, formatterOptions) ||
    formatFairPriceDeltaFromListing(listing, formatterOptions);
  const comparablePriceLabel = formatComparablePriceLabel(listing, formatterOptions);
  const confidenceLabel = formatConfidenceLabel(listing, formatterOptions);

  let sampleLabel = null;
  if (sampleSize > 0) {
    sampleLabel = t
      ? t("automotive.detail.fairPrice.sampleSize", { count: sampleSize })
      : `Basado en ${sampleSize} ${sampleSize === 1 ? "publicación comparable" : "publicaciones comparables"}`;
  }

  let lastUpdatedLabel = null;
  if (listing?.fairPriceUpdatedAt) {
    try {
      const dateLabel = new Date(listing.fairPriceUpdatedAt).toLocaleDateString(locale);
      lastUpdatedLabel = t
        ? t("automotive.detail.fairPrice.lastUpdated", { date: dateLabel })
        : `Actualizado ${dateLabel}`;
    } catch {
      lastUpdatedLabel = null;
    }
  }

  const description = getFairPriceBuyerDescription(label, listing, { t });
  const detailTitle = t
    ? resolveCatalogLabel(
        t,
        `automotive.detail.fairPrice.titles.${label}`,
        FAIR_PRICE_DETAIL_TITLES_ES[label] || FAIR_PRICE_DETAIL_TITLES_ES.unknown
      )
    : FAIR_PRICE_DETAIL_TITLES_ES[label] || FAIR_PRICE_DETAIL_TITLES_ES.unknown;

  return {
    label,
    displayLabel: formatFairPriceLabel(label, { t }),
    shortLabel: formatFairPriceLabel(label, { short: true, t }),
    description,
    detailTitle,
    badgeTone: getFairPriceBadgeTone(label),
    deltaLabel,
    confidenceLabel,
    sampleSize,
    sampleLabel,
    comparablePriceLabel,
    lastUpdatedLabel,
    hasEnoughData,
    buyerNote: description,
    showBadge: shouldShowFairPriceBadge(listing),
  };
}
